import { useState, useCallback } from "react";

// Persist a per-user map of collapse states in localStorage.
// key is namespaced with the username so different users on the same browser don't clash.
// defaultCollapsed is used for ids without a stored preference.
export function usePersistentCollapse(scope, username, defaultCollapsed = false) {
  const storageKey = `tw1_collapse_${scope}_${username || "anon"}`;

  const [map, setMap] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  // fallback: per-id default when the user never toggled this id (own toggle always wins)
  const isCollapsed = useCallback(
    (id, fallback = defaultCollapsed) => (id in map ? !!map[id] : fallback),
    [map, defaultCollapsed]
  );

  const toggle = useCallback((id, fallback = defaultCollapsed) => {
    setMap((prev) => {
      const current = id in prev ? !!prev[id] : fallback;
      const next = { ...prev, [id]: !current };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [storageKey, defaultCollapsed]);

  return { isCollapsed, toggle };
}
